import { API } from "@js/api";
import type { PaginatedResult } from "@js/types/generic";

import type {
  Roster,
  RosterFreelancer,
  RosterSummary,
  RosterVisibilityType,
} from "./types";

export type GetRostersParams = Partial<{
  search: string;
  visibility: RosterVisibilityType;
  page: number;
  page_size: number;
}>;

export type GetRostersResponse = PaginatedResult<RosterSummary>;

export type CreateRosterPayload = {
  title: string;
  description?: string;
  visibility?: RosterVisibilityType;
};

export type UpdateRosterPayload = {
  id: number;
} & Partial<CreateRosterPayload>;

export type UpdateRosterFreelancerNotePayload = {
  id: number;
  freelancerId: number;
  note: string;
};

export type BulkUpdateRosterFreelancersPayload = {
  id: number;
  freelancer_ids: number[];
  note?: string;
};

export const rostersApi = API.injectEndpoints({
  endpoints: (build) => ({
    getRosters: build.query<GetRostersResponse, GetRostersParams | void>({
      query: (params) => ({
        url: "/rosters/",
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Rosters", id: "LIST" }],
    }),
    getRoster: build.query<Roster, number>({
      query: (id) => ({
        url: `/rosters/${id}/`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [{ type: "Rosters", id }],
    }),
    createRoster: build.mutation<Roster, CreateRosterPayload>({
      query: (data) => ({
        url: "/rosters/",
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: "Rosters", id: "LIST" }],
    }),
    updateRoster: build.mutation<Roster, UpdateRosterPayload>({
      query: ({ id, ...data }) => ({
        url: `/rosters/${id}/`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Rosters", id },
        "Rosters",
      ],
    }),
    deleteRoster: build.mutation<void, number>({
      query: (id) => ({
        url: `/rosters/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Rosters", id: "LIST" }],
    }),
    addRosterFreelancers: build.mutation<
      Roster,
      BulkUpdateRosterFreelancersPayload
    >({
      query: ({ id, ...data }) => ({
        url: `/rosters/${id}/add_freelancers/`,
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Rosters", id },
        { type: "Rosters", id: "LIST" },
      ],
    }),
    removeRosterFreelancers: build.mutation<
      Roster,
      Omit<BulkUpdateRosterFreelancersPayload, "note">
    >({
      query: ({ id, freelancer_ids }) => ({
        url: `/rosters/${id}/remove_freelancers/`,
        method: "POST",
        data: { freelancer_ids },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Rosters", id }],
    }),
    updateRosterFreelancerNote: build.mutation<
      RosterFreelancer,
      UpdateRosterFreelancerNotePayload
    >({
      query: ({ id, freelancerId, note }) => ({
        url: `/rosters/${id}/freelancers/${freelancerId}/note/`,
        method: "PATCH",
        data: { note },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "Rosters", id }],
    }),
  }),
});

export const {
  useGetRostersQuery,
  useGetRosterQuery,
  useCreateRosterMutation,
  useUpdateRosterMutation,
  useDeleteRosterMutation,
  useAddRosterFreelancersMutation,
  useRemoveRosterFreelancersMutation,
  useUpdateRosterFreelancerNoteMutation,
} = rostersApi;
